import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { getCheckoutStatus, session, getLeadByToken } from "../lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8;

export default function PaymentReturn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const s = await getCheckoutStatus(sessionId);
        if (s.payment_status === "paid") {
          setState("paid");
          // refresh lead in session if we have token
          const local = session.load();
          if (local?.session_token) {
            try {
              const fresh = await getLeadByToken(local.session_token);
              session.save(fresh);
            } catch (refreshErr) {
              // Non-fatal: lead refresh failed, but payment already confirmed
              console.warn("Lead refresh after payment failed:", refreshErr);
            }
          }
          return;
        }
        if (s.status === "expired") { setState("expired"); return; }
        attempts.current += 1;
        if (attempts.current >= MAX_POLL_ATTEMPTS) { setState("failed"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (pollErr) {
        console.warn("Payment poll attempt failed:", pollErr);
        attempts.current += 1;
        if (attempts.current >= MAX_POLL_ATTEMPTS) { setState("failed"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="wizard-shell">
      <div className="wizard-frame fade-in">
        <header className="px-5 py-4 border-b border-slate-100">
          <Logo size={32} withWordmark />
        </header>
        <main className="flex-1 flex flex-col px-6 py-8 items-center justify-center text-center">
          {state === "polling" && (
            <>
              <Loader2 className="w-14 h-14 text-[var(--el-red)] animate-spin" />
              <h1 className="mt-6 text-2xl font-[Outfit] font-extrabold text-slate-900">
                Confirmando tu pago
              </h1>
              <p className="mt-2 text-sm text-slate-600 max-w-[22rem]">
                Un momento, estamos verificando el pago con la pasarela segura.
              </p>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="h-16 w-16 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9" />
              </div>
              <h1 className="mt-5 text-2xl font-[Outfit] font-extrabold text-slate-900">
                ¡Pago confirmado!
              </h1>
              <p className="mt-2 text-sm text-slate-600 max-w-[22rem]">
                Recibirás tu prototipo personalizado en pocos minutos. Te hemos enviado un email con los detalles.
              </p>
              <Button onClick={() => navigate("/panel")}
                className="btn-primary h-12 w-full rounded-2xl mt-6 font-semibold"
                data-testid="btn-go-panel">
                Ir a mi panel
              </Button>
            </>
          )}
          {(state === "failed" || state === "expired") && (
            <>
              <div className="h-16 w-16 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center">
                <XCircle className="w-9 h-9" />
              </div>
              <h1 className="mt-5 text-2xl font-[Outfit] font-extrabold text-slate-900">
                No pudimos confirmar el pago
              </h1>
              <p className="mt-2 text-sm text-slate-600 max-w-[22rem]">
                Si el cargo fue realizado lo verás reflejado pronto. Si no, vuelve a intentar.
              </p>
              <Button onClick={() => navigate("/onboarding")}
                className="btn-primary h-12 w-full rounded-2xl mt-6 font-semibold">
                Volver al onboarding
              </Button>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
